import 'twin.macro';
import React from 'react';
import tw from 'twin.macro';

import { LogoPlace } from 'types/adEditorTypes';

/**
 * SYM-6301, shared since SYM-6353. The four places a logo can sit, in reading
 * order, with the words the customer sees.
 *
 * ONE ROW FOR THREE SCREENS: the image-ad editor, the Bring to Life pop-up and
 * the Style step of the Track 1 video. The same four values go to the server
 * from all three, so the same four buttons with the same four words ask for
 * them.
 *
 * "Top" is in every label on purpose: bottom and centre can be added later
 * without any of these three meaning something different than they did.
 */
export const LOGO_PLACE_BUTTONS: { value: LogoPlace; label: string }[] = [
  { value: 'top-left', label: 'Top left' },
  { value: 'top-middle', label: 'Top middle' },
  { value: 'top-right', label: 'Top right' },
  { value: 'none', label: 'No logo' },
];

/** The words for one place, e.g. "Top right", or the value itself if unknown. */
export const logoPlaceLabel = (value: LogoPlace): string =>
  LOGO_PLACE_BUTTONS.find((b) => b.value === value)?.label ?? value;

/**
 * One button in the Logo row. SYM-6301.
 *
 * Disabled rather than hidden when the ad has no logo file, so a customer who has not uploaded one
 * still learns the choice exists -- Steven's call on 21 Sep 2026, against hiding the row. The line
 * under the row says why the buttons do nothing.
 */
export const LogoButton = ({
  label,
  lit,
  disabled = false,
  onClick,
}: {
  label: string;
  lit: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={lit}
    css={[
      tw`text-sm px-3 py-1.5 rounded border`,
      lit
        ? tw`bg-gray-900 border-gray-900 text-white font-semibold`
        : tw`bg-white border-gray-300 text-gray-700`,
      disabled && tw`opacity-50 cursor-not-allowed`,
    ]}
  >
    {label}
  </button>
);

/**
 * The four buttons, without a container: the caller owns the row around them,
 * because the image-ad editor puts its own two-button variant in the same row.
 */
export const LogoPlaceButtons = ({
  value,
  disabled = false,
  onPick,
}: {
  /** The place that is lit. */
  value: LogoPlace | null | undefined;
  disabled?: boolean;
  onPick: (place: LogoPlace) => void;
}) => (
  <>
    {LOGO_PLACE_BUTTONS.map(({ value: place, label }) => (
      <LogoButton
        key={place}
        label={label}
        lit={value === place}
        disabled={disabled}
        onClick={() => onPick(place)}
      />
    ))}
  </>
);

export default LogoPlaceButtons;
